import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import {
  BREAKPOINTS,
  useMediaQuery,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkBubble from '@skyscanner-internal/backpack-web/bpk-component-bubble';
import BpkSelectableChip, {
  CHIP_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-chip';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import BpkBaggageGeneric from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/baggage-generic';
import BpkBeach from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/beach';
import BpkIconCars from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/cars';
import BpkIconFlight from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/flight';
import BpkIconHotels from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/hotels';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import { useBehaviourTracking } from '@web-platform/shared-banana-tracking/src/trackMBD';
import { ERROR_BOUNDARY_PREFIX_CAPYBARA } from '@web-platform/shared-universal-constants/src/errorBoundary';
import { SCREEN_NAMES } from '@web-platform/shared-universal-constants/src/screenName';
import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import {
  trackChokePoint,
  trackSmartMetric,
} from '@web-platform/shared-universal-tracking/src/trackExperiment';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import { MobileScrollContainer } from '@web-platform/shared-universal-ui-mobile-scroll-container';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { logOperationalEvent } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import classNames from 'classnames';
import { redirect } from 'saddlebag-browser';

// Cruise icon used only for the short-lived cruises tab experiment.
import { CruiseIcon } from './CruiseIcon';
import { TABS, useGlobalTabsData } from './useGlobalTabsData';

import type { TabDataType, TabTypes } from './useGlobalTabsData';
import type { ScreenName } from '@web-platform/shared-universal-constants/src/screenName';

import STYLES from './GlobalTabs.module.scss';

const RtlSupportedBpkIconCars = withRtlSupport(BpkIconCars);
const RtlSupportedBpkIconFlight = withRtlSupport(BpkIconFlight);
const RtlSupportedBpkIconPackagesBeach = withRtlSupport(BpkBeach);
const RtlSupportedBpkIconBaggageGeneric = withRtlSupport(BpkBaggageGeneric);
const RtlSupportedCruiseIcon = withRtlSupport(CruiseIcon);

type Props = {
  compactTabs?: boolean;
  enableHeaderReducedVerticalSpacing?: boolean;
  primaryNavInFss?: boolean;
  screenName: ScreenName;
};

export const PackageExperimentVersions = {
  NO_VISUAL_CHANGES_WITH_NEW_BUBBLE: 'NO_VISUAL_CHANGES_WITH_NEW_BUBBLE',
  VISUAL_CHANGE_ONLY: 'VISUAL_CHANGE_ONLY',
  VISUAL_CHANGE_AND_NEW_BUBBLE: 'VISUAL_CHANGE_AND_NEW_BUBBLE',
};

export const GlobalTabs = ({
  compactTabs = false,
  enableHeaderReducedVerticalSpacing = false,
  primaryNavInFss = false,
  screenName,
}: Props): ReactElement => {
  useEffect(() => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_TABS_LOADED.NAME,
      strings: {
        screenName,
      },
    });
  }, [screenName]);

  const isPackageExperimentEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.PACKAGES_HEADER_TAB_ENABLED,
  );

  const isStaysHeaderTabBubbleEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.HOTELS_HEADER_TAB_BUBBLE_ENABLED,
  );

  const packageExperimentVersion = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.EXPERIMENT.PACKAGES_HEADER_TAB_VERSION,
  );

  const isVisualChangeEnabled = [
    PackageExperimentVersions.VISUAL_CHANGE_AND_NEW_BUBBLE,
    PackageExperimentVersions.VISUAL_CHANGE_ONLY,
  ].includes(packageExperimentVersion);

  const isPackagesBubbleEnabled = [
    PackageExperimentVersions.NO_VISUAL_CHANGES_WITH_NEW_BUBBLE,
    PackageExperimentVersions.VISUAL_CHANGE_AND_NEW_BUBBLE,
  ].includes(packageExperimentVersion);
  const showPackageVisualChanges = isPackageExperimentEnabled && isVisualChangeEnabled;
  const showPackagesBubble = isPackageExperimentEnabled && isPackagesBubbleEnabled;
  const showHeaderReducedVerticalSpacing = showPackagesBubble && enableHeaderReducedVerticalSpacing;

  const showBeachIcon = showPackageVisualChanges || showPackagesBubble;

  const CHIP_ICONS = {
    [TABS.HOTELS]: <BpkIconHotels />,
    [TABS.CARHIRE]: <RtlSupportedBpkIconCars />,
    [TABS.FLIGHTS]: <RtlSupportedBpkIconFlight />,
    [TABS.PACKAGES]: showBeachIcon ? (
      <RtlSupportedBpkIconPackagesBeach />
    ) : (
      <RtlSupportedBpkIconBaggageGeneric />
    ),
    [TABS.CRUISES]: <RtlSupportedCruiseIcon />,
  };

  const [selectedTab, setSelectedTab] = useState<TabTypes>(TABS.FLIGHTS);

  const isSmallScreen = useMediaQuery(BREAKPOINTS.SMALL_TABLET);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);

  const tabsData = useGlobalTabsData(screenName);

  const componentName =
    screenName === SCREEN_NAMES.HOME_PAGE ? 'search_controls_navigation' : 'global_navigation';

  const { ref: hotelTabRef } = useBehaviourTracking({
    componentName,
    vertical: 'hotel',
  });

  const { ref: carTabRef } = useBehaviourTracking({
    componentName,
    vertical: 'car_hire',
  });

  const { ref: cruisesRef } = useBehaviourTracking({
    componentName,
    vertical: 'cruise',
  });

  const getTabRef = (type: TabTypes) => {
    if (type === TABS.HOTELS) {
      return hotelTabRef;
    }
    if (type === TABS.CARHIRE) {
      return carTabRef;
    }
    if (type === TABS.CRUISES) {
      return cruisesRef;
    }

    return undefined;
  };

  const handleClick = (data: TabDataType) => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_TABS_TAPPED.NAME,
      strings: {
        tab: data.tabType,
        screenName,
      },
    });

    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_TABS_TAPPED.NAME,
      tab: data.tabType,
    });

    trackSmartMetric(`HEADER_${data.tabType}_Tab_Clicked`);

    if (data.tabType === TABS.HOTELS) {
      trackChokePoint(`header_hotels_tab`);
    }

    if (data.url) {
      if (!data.openInNewTab) {
        setSelectedTab(data.tabType);
      }
      redirect(data.url, data.openInNewTab ?? false);
    }
  };

  const renderTabs = () => {
    if (compactTabs) {
      return renderCompactTabs();
    }
    return renderFullTabs();
  };

  const renderFullTabs = () => (
    <div
      role="tablist"
      data-testid="tabContainer"
      className={classNames(STYLES.container, {
        [STYLES.containerNoPadding]: primaryNavInFss,
        [STYLES.packagesExperimentContainer]: isPackageExperimentEnabled,
        [STYLES.packagesExperimentContainerWithBubble]:
          showPackagesBubble || isStaysHeaderTabBubbleEnabled,
        [STYLES.packagesExperimentContainerWithCompactSpacing]: showHeaderReducedVerticalSpacing,
      })}
    >
      {tabsData.map((data) => {
        const isSelected = selectedTab === data.tabType;
        return (
          <div ref={getTabRef(data.tabType)} key={data.tabType} className={STYLES.wrapper}>
            <BpkSelectableChip
              role="tab"
              accessibilityLabel={data.tabText}
              aria-selected={isSelected}
              selected={isSelected}
              onClick={() => handleClick(data)}
              type={CHIP_TYPES.onDark}
              className={classNames(STYLES.tab, {
                [STYLES.packagesExperimentTab]: isPackageExperimentEnabled && isMobile,
                [STYLES.selected]: isSelected,
              })}
              leadingAccessoryView={CHIP_ICONS[data.tabType]}
            >
              <BpkText tagName="span" textStyle={TEXT_STYLES.label2}>
                {data.tabText}
              </BpkText>
              {data.tabType === TABS.PACKAGES &&
                showPackagesBubble &&
                !isStaysHeaderTabBubbleEnabled && (
                  <div className={STYLES.bubbleContainer}>
                    <BpkBubble>{translate('ktxtHeader_new_bubble')}</BpkBubble>
                  </div>
                )}
              {data.tabType === TABS.HOTELS && isStaysHeaderTabBubbleEnabled && (
                <div className={STYLES.bubbleContainer}>
                  <BpkBubble>{translate('ktxtHeader_new_bubble')}</BpkBubble>
                </div>
              )}
            </BpkSelectableChip>
          </div>
        );
      })}
    </div>
  );

  const renderCompactTabs = () => (
    <div role="tablist" className={STYLES.compactPrimaryNavTabList}>
      {tabsData.map((data) => {
        const isSelected = selectedTab === data.tabType;
        return (
          <button
            type="button"
            key={data.tabType}
            ref={getTabRef(data.tabType)}
            role="tab"
            className={STYLES.compactPrimaryNavTabItem}
            aria-label={data.tabText}
            aria-selected={isSelected}
            onClick={() => handleClick(data)}
          >
            {isSelected ? (
              <div className={STYLES.compactPrimaryNavSelectedTab}>
                {CHIP_ICONS[data.tabType]}
                <BpkText textStyle={TEXT_STYLES.label2}>{data.tabText}</BpkText>
              </div>
            ) : (
              <div aria-hidden="true" className={STYLES.compactPrimaryNavIcon}>
                {CHIP_ICONS[data.tabType]}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );

  const packagesExperimentScrollContainerWithCompactSpacingClassName =
    showHeaderReducedVerticalSpacing
      ? STYLES.packagesExperimentScrollContainerWithCompactSpacing
      : undefined;

  return (
    <ErrorBoundary componentName={`${ERROR_BOUNDARY_PREFIX_CAPYBARA}_GlobalTabs`} fallback={null}>
      {isSmallScreen ? (
        <MobileScrollContainer
          containerClassName={packagesExperimentScrollContainerWithCompactSpacingClassName}
        >
          {renderTabs()}
        </MobileScrollContainer>
      ) : (
        renderTabs()
      )}
    </ErrorBoundary>
  );
};
